'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import { createEmbeddedContext, createReadyMessage, createSyncAckMessage, isEmbeddedPropsChangedMessage, isSyncMessage, validMessage } from 'ui-addon-iframeable/messaging/IFrameControlMessage';
import { getEmbeddedPropsFromQueryParams, getTopLevelEmbeddedContextDefaults, isTopLevelWindowSupported } from 'ui-addon-iframeable/utils/Embedded';
import { logEmbedError, logEmbedMessageReceived, logEmbedMessageSent } from 'ui-addon-iframeable/utils/Logger';
import { ERROR_EMBED_CONTEXT_INVALID_RECEIVE_MESSAGE } from 'ui-addon-iframeable/messaging/Errors';
import emptyFunction from 'react-utils/emptyFunction';
import { isMessageToMe, validFormat } from 'ui-addon-iframeable/messaging/IFrameMessage'; // To allow your EmbeddedComponent to render outside of an iframe:
// set `enableTopLevelEmbed` to render in ALL environments including prod (ensure this will not have adverse consequences!)
// set `enableTopLevelEmbedForTestOnly` to render ONLY in the QA environment

export default ((EmbeddedComponent, {
  enableTopLevelEmbed = false,
  enableTopLevelEmbedForTestOnly = false
} = {}) => {
  class UIEmbeddedComponent extends PureComponent {
    constructor() {
      super();
      this.handleOnReady = this.handleOnReady.bind(this);
      this.handleReceiveMessage = this.handleReceiveMessage.bind(this);
      this.sendMessage = this.sendMessage.bind(this);
      this.callbacks = {};
      this.parent = {
        origin: null
      };
      this.child = {
        embeddedContext: null,
        receiveMessage: emptyFunction
      };
      this.state = {
        embeddedProps: {},
        synced: false
      };
    }

    componentDidMount() {
      window.addEventListener('message', this.handleReceiveMessage, false);

      if (isTopLevelWindowSupported({
        enableTopLevelEmbed,
        enableTopLevelEmbedForTestOnly
      })) {
        this.initWhenTopLevel();
      }
    }

    componentWillUnmount() {
      window.removeEventListener('message', this.handleReceiveMessage);
    }

    createEmbeddedContext({
      embedId,
      group,
      name,
      appData
    }, sendMessageFn = this.sendMessage) {
      return createEmbeddedContext({
        embedId,
        group,
        name,
        app: appData,
        sendMessageFn
      });
    }

    initEmbeddedContext(message) {
      this.child.embeddedContext = this.createEmbeddedContext(message.payload);
    }

    initWhenTopLevel() {
      this.child.embeddedContext = this.createEmbeddedContext(getTopLevelEmbeddedContextDefaults(), emptyFunction);
      this.setState({
        synced: true,
        embeddedProps: getEmbeddedPropsFromQueryParams()
      });
    }

    validMessage(event) {
      if (!this.parent.origin) {
        if (validFormat(event.data)) {
          this.parent.origin = event.origin;
        }
      }

      return validMessage(event, this.parent.origin);
    }

    isToEmbeddedComponent(message) {
      return isMessageToMe(message, this.child.embeddedContext.id, this.child.embeddedContext.group);
    }

    hasCallback(message) {
      return !!this.callbacks[message.id];
    }

    dispatchCallback(message) {
      this.callbacks[message.id](message.payload);
      delete this.callbacks[message.id];
    }

    sendMessage(message, callback) {
      if (callback) {
        this.callbacks[message.id] = callback;
      }

      window.parent.postMessage(message, this.parent.origin);
      logEmbedMessageSent(this.child.embeddedContext.name, message);
    }

    logMessageReceived(message) {
      if (!this.child.embeddedContext) {
        return;
      }

      logEmbedMessageReceived(this.child.embeddedContext.name, message);
    }

    handleOnReady(childContext) {
      if (typeof childContext.receiveMessage !== 'function') {
        logEmbedError(this.child.embeddedContext.name, ERROR_EMBED_CONTEXT_INVALID_RECEIVE_MESSAGE, childContext);
        return;
      }

      this.child.receiveMessage = childContext.receiveMessage;
      const readyMessage = createReadyMessage(this.child.embeddedContext.id, childContext.data);
      this.sendMessage(readyMessage);
    }

    handleSyncMessage(message) {
      if (this.state.synced) {
        return;
      }

      this.sendMessage(createSyncAckMessage(message));
      this.setState({
        synced: true,
        embeddedProps: message.payload.embeddedProps
      });
    }

    handleEmbeddedPropsChangedMessage(message) {
      const _message$payload = message.payload,
            embeddedProps = _objectWithoutPropertiesLoose(_message$payload, ["type"]);

      this.setState({
        embeddedProps
      });
    }

    handleReceiveMessage(event) {
      if (!this.validMessage(event)) {
        return;
      }

      const {
        data: message
      } = event;

      if (isSyncMessage(message) && !this.state.synced) {
        this.initEmbeddedContext(message);
      }

      this.logMessageReceived(message);

      if (isSyncMessage(message)) {
        this.handleSyncMessage(message);
      } else if (isEmbeddedPropsChangedMessage(message)) {
        this.handleEmbeddedPropsChangedMessage(message);
      } else if (this.state.synced) {
        if (this.hasCallback(message)) {
          this.dispatchCallback(message);
        } else if (this.isToEmbeddedComponent(message)) {
          this.child.receiveMessage(message);
        }
      }
    }

    render() {
      if (!this.state.synced) {
        return null;
      }

      return /*#__PURE__*/_jsx(EmbeddedComponent, Object.assign({}, this.props, {}, this.state.embeddedProps, {
        onReady: this.handleOnReady,
        embeddedContext: this.child.embeddedContext
      }));
    }

  }

  UIEmbeddedComponent.EmbeddedComponent = EmbeddedComponent;
  return UIEmbeddedComponent;
});